'use client'

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react'
import { cn } from '../../../lib/utils'
import Icon from '../Icon'
import { ResponsivePopover } from '../ResponsivePopover'

export type PageTypeaheadResult = {
  id: string
  title: string
  path: string
  visibility?: 'team' | 'private' | string
}

export interface PageTypeaheadPopoverProps {
  trigger: React.ReactNode
  title?: string
  placeholder?: string
  emptyMessage?: string
  onSearch: (query: string) => Promise<PageTypeaheadResult[]>
  onSelect: (page: PageTypeaheadResult) => void | Promise<void>
  popoverAlign?: 'start' | 'center' | 'end'
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

const itemClassName = (selected: boolean) =>
  cn(
    'w-full flex items-center gap-2 px-2 h-9 text-left text-[11px] text-foreground/80',
    'hover:bg-muted/60 transition-colors rounded-sm',
    selected && 'bg-muted/60',
  )

function PageTypeaheadList({
  pages,
  selectedIndex,
  isLoading,
  emptyMessage,
  onSelect,
}: {
  pages: PageTypeaheadResult[]
  selectedIndex: number
  isLoading: boolean
  emptyMessage: string
  onSelect: (page: PageTypeaheadResult) => void
}) {
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const selectedElement = listRef.current.children[
        selectedIndex
      ] as HTMLElement
      selectedElement?.scrollIntoView({ block: 'nearest' })
    }
  }, [selectedIndex])

  if (isLoading) {
    return (
      <div className="py-2 px-2 text-[11px] text-muted-foreground/70 text-center">
        Searching...
      </div>
    )
  }

  if (pages.length === 0) {
    return (
      <div className="py-2 px-2 text-[11px] text-muted-foreground/70 text-center">
        {emptyMessage}
      </div>
    )
  }

  return (
    <div
      ref={listRef}
      className="max-h-[220px] overflow-y-auto overflow-x-hidden py-0.5"
    >
      {pages.map((page, index) => (
        <button
          key={page.id}
          type="button"
          onClick={() => onSelect(page)}
          className={itemClassName(selectedIndex === index)}
        >
          <div className="h-4 w-4 rounded-sm bg-muted/80 flex items-center justify-center flex-shrink-0">
            <Icon
              name="FileText"
              size={10}
              className="text-muted-foreground/80"
            />
          </div>
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="truncate leading-4 font-medium">{page.title}</div>
            <div className="truncate text-[9px] leading-3 text-muted-foreground/70">
              /{page.path}
              {page.visibility ? ` · ${page.visibility}` : ''}
            </div>
          </div>
        </button>
      ))}
    </div>
  )
}

export function PageTypeaheadPanel({
  placeholder = 'Search pages...',
  emptyMessage = 'No pages found',
  onSearch,
  onSelect,
  onCreateNewPage,
  createNewPageLabel = 'Create New Page',
  onClose,
}: {
  placeholder?: string
  emptyMessage?: string
  onSearch: (query: string) => Promise<PageTypeaheadResult[]>
  onSelect: (page: PageTypeaheadResult) => void | Promise<void>
  onCreateNewPage?: () => void
  createNewPageLabel?: string
  onClose?: () => void
}) {
  const [query, setQuery] = useState('')
  const [pages, setPages] = useState<PageTypeaheadResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isSelecting, setIsSelecting] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      inputRef.current?.focus()
    }, 0)

    return () => window.clearTimeout(timeout)
  }, [])

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)

    const searchTimeout = window.setTimeout(async () => {
      try {
        const results = await onSearch(query)
        if (!cancelled) {
          setPages(results)
          setSelectedIndex(0)
        }
      } catch (error) {
        console.error('Failed to search pages:', error)
        if (!cancelled) {
          setPages([])
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }, 200)

    return () => {
      cancelled = true
      window.clearTimeout(searchTimeout)
    }
  }, [onSearch, query])

  const handleSelect = useCallback(
    async (page: PageTypeaheadResult) => {
      if (isSelecting) return

      setIsSelecting(true)
      try {
        await onSelect(page)
        onClose?.()
      } finally {
        setIsSelecting(false)
      }
    },
    [isSelecting, onClose, onSelect],
  )

  const handleInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (pages.length === 0) {
        if (event.key === 'Escape') {
          event.preventDefault()
          onClose?.()
        }
        return
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          setSelectedIndex((prev) => Math.min(prev + 1, pages.length - 1))
          break
        case 'ArrowUp':
          event.preventDefault()
          setSelectedIndex((prev) => Math.max(prev - 1, 0))
          break
        case 'Enter':
          event.preventDefault()
          {
            const selected = pages[selectedIndex]
            if (selected) {
              void handleSelect(selected)
            }
          }
          break
        case 'Escape':
          event.preventDefault()
          onClose?.()
          break
      }
    },
    [handleSelect, onClose, pages, selectedIndex],
  )

  return (
    <div className="flex flex-col gap-1 min-w-[240px]">
      <div className="relative">
        <Icon
          name="Search"
          size={14}
          className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground/60 pointer-events-none"
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          disabled={isSelecting}
          className={cn(
            'w-full h-8 rounded-md border border-border/60 bg-background pl-7 pr-2',
            'text-xs text-foreground placeholder:text-muted-foreground/50',
            'outline-none focus-visible:ring-1 focus-visible:ring-ring',
          )}
        />
      </div>
      <PageTypeaheadList
        pages={pages}
        selectedIndex={selectedIndex}
        isLoading={isLoading}
        emptyMessage={emptyMessage}
        onSelect={(page) => {
          void handleSelect(page)
        }}
      />
      {onCreateNewPage ? (
        <div className="border-t border-border/60 pt-1">
          <button
            type="button"
            className={itemClassName(false)}
            onClick={() => {
              onCreateNewPage()
              onClose?.()
            }}
          >
            <div className="h-4 w-4 rounded-sm bg-muted/80 flex items-center justify-center flex-shrink-0">
              <Icon
                name="Plus"
                size={10}
                className="text-muted-foreground/80"
              />
            </div>
            <span className="truncate leading-4 font-medium">
              {createNewPageLabel}
            </span>
          </button>
        </div>
      ) : null}
    </div>
  )
}

export function PageTypeaheadPopover({
  trigger,
  title = 'Add page',
  placeholder = 'Search pages...',
  emptyMessage = 'No pages found',
  onSearch,
  onSelect,
  popoverAlign = 'end',
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: PageTypeaheadPopoverProps) {
  const [internalOpen, setInternalOpen] = useState(false)

  const isControlled = controlledOpen !== undefined
  const isOpen = isControlled ? controlledOpen : internalOpen

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(nextOpen)
      }
      controlledOnOpenChange?.(nextOpen)
    },
    [controlledOnOpenChange, isControlled],
  )

  return (
    <ResponsivePopover
      open={isOpen}
      onOpenChange={handleOpenChange}
      trigger={trigger}
      title={title}
      popoverAlign={popoverAlign}
      popoverClassName="w-72 p-2"
      drawerClassName="px-0"
    >
      {isOpen ? (
        <PageTypeaheadPanel
          placeholder={placeholder}
          emptyMessage={emptyMessage}
          onSearch={onSearch}
          onSelect={onSelect}
          onClose={() => handleOpenChange(false)}
        />
      ) : null}
    </ResponsivePopover>
  )
}
